"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, CloudUpload, Filter, X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { QRCode } from "react-qrcode-logo";
import { localeFlags } from "@/components/RouteLanguageSwitcher";
import UploadPrintTile, { type UploadTileStatus } from "@/components/upload/UploadPrintTile";
import UploadProgressMeter from "@/components/upload/UploadProgressMeter";
import UploadSuccessFan from "@/components/upload/UploadSuccessFan";
import { cn } from "@/lib/utils";
import { DEMO_COVER_PHOTO, DEMO_PHOTOS } from "./photowallDemoPhotos";
export type DemoPhoneStage = "scan" | "gallery" | "picking" | "uploading" | "done" | "after";
export interface DemoPhonePickerLabels {
  cancel: string;
  title: string;
  add: string;
}
interface PhotowallDemoPhoneProps {
  stage: DemoPhoneStage;
  eventName: string;
  memoryCount: number;
  pickerLabels?: DemoPhonePickerLabels;
  className?: string;
}

// The guest page is drawn at a real phone's width and scaled into the frame,
// so it can use the upload page's own classes and upload components.
const SCREEN_WIDTH = 390;
const SCREEN_HEIGHT = 844;
const STATUS_BAR_HEIGHT = 47;
const UPLOAD_DURATION_MS = 2300;
const DEFAULT_PICKER_LABELS: DemoPhonePickerLabels = {
  cancel: "Cancel",
  title: "Recents",
  add: "Add"
};
// Three reception photos a guest might send.
const PICKED_PHOTOS = [DEMO_PHOTOS[13], DEMO_PHOTOS[15], DEMO_PHOTOS[14]];
const PICKER_PHOTOS = [11, 13, 12, 15, 16, 14, 19, 20, 18, 17, 10, 21, 22, 6, 9, 5, 7, 4].map(index => DEMO_PHOTOS[index]);
const GALLERY_PHOTOS = [5, 9, 7, 6, 10, 4, 8, 3, 1, 12, 2, 0].map(index => DEMO_PHOTOS[index]);
const PICKED_PREVIEWS = PICKED_PHOTOS.map(photo => ({
  url: photo.thumbnailUrl
}));
const CONFETTI = Array.from({
  length: 14
}, (_, index) => ({
  color: ["#F9B144", "#FFCE7F", "#FED7AA", "#E3971D", "#FFFFFF"][index % 5],
  x: index * 37 % 100 - 50,
  delay: index % 5 * 60,
  rotate: index * 53 % 360
}));

// Files upload side by side and finish a little apart, like a real batch.
function getTile(progress: number, index: number): {
  status: UploadTileStatus;
  percentage: number;
} {
  const position = progress * 1.6 - index * 22;
  const percentage = Math.min(100, Math.max(0, position));
  if (percentage < 100) return {
    status: "uploading",
    percentage
  };
  return {
    status: position >= 116 ? "success" : "saving",
    percentage
  };
}

/** A guest's phone on the real upload page: scan, pick, upload, and the saved photos. */
export default function PhotowallDemoPhone(t0) {
  const $ = _c(149);
  const {
    stage,
    eventName,
    memoryCount,
    pickerLabels: t1,
    className
  } = t0;
  const pickerLabels = t1 === undefined ? DEFAULT_PICKER_LABELS : t1;
  const t = useTranslations("Upload");
  const locale = useLocale();
  const screenRef = useRef(null);
  const [scale, setScale] = useState(0);
  const [progress, setProgress] = useState(0);
  const isUploading = stage === "uploading";
  const isDialogShown = isUploading || stage === "done";
  const hasNewTiles = stage === "after";
  let t2;
  let tiles;
  if ($[0] !== progress) {
    tiles = PICKED_PHOTOS.map((_, index) => getTile(progress, index));
    t2 = tiles.filter(_temp);
    $[0] = progress;
    $[1] = t2;
    $[2] = tiles;
  } else {
    t2 = $[1];
    tiles = $[2];
  }
  const savedCount = t2.length;
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const element = screenRef.current;
      if (!element) {
        return;
      }
      const observer = new ResizeObserver(t5 => {
        const [entry] = t5;
        return setScale(entry.contentRect.width / SCREEN_WIDTH);
      });
      observer.observe(element);
      return () => observer.disconnect();
    };
    t4 = [];
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== isUploading || $[6] !== stage) {
    t5 = () => {
      if (!isUploading) {
        setProgress(stage === "done" || stage === "after" ? 100 : 0);
        return;
      }
      const startedAt = performance.now();
      const timer = setInterval(() => {
        setProgress(Math.min(100, (performance.now() - startedAt) / UPLOAD_DURATION_MS * 100));
      }, 90);
      return () => clearInterval(timer);
    };
    t6 = [isUploading, stage];
    $[5] = isUploading;
    $[6] = stage;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== className) {
    t7 = cn("relative rounded-[clamp(18px,3vw,36px)] bg-[#1a1614] p-[clamp(3px,0.45vw,6px)] shadow-[0_30px_60px_-20px_rgba(30,18,8,0.7),inset_0_0_0_1px_rgba(255,255,255,0.08)]", className);
    $[9] = className;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      aspectRatio: `${SCREEN_WIDTH} / ${SCREEN_HEIGHT}`
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = `scale(${scale})`;
  let t10;
  if ($[12] !== t9) {
    t10 = {
      width: SCREEN_WIDTH,
      height: SCREEN_HEIGHT,
      transform: t9
    };
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  let t12;
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      paddingTop: STATUS_BAR_HEIGHT,
      backgroundImage: "linear-gradient(180deg,#faf6f0 0%,#f7f2eb 28%,#f4efe8 38rem)",
      backgroundRepeat: "no-repeat"
    };
    t12 = <div className="pointer-events-none absolute left-[-7rem] top-[-5rem] h-56 w-56 rounded-full bg-[#ffd9b3]/30 blur-3xl" />;
    t13 = <div className="pointer-events-none absolute left-1/3 top-[24rem] h-48 w-48 rounded-full bg-[#ffb15f]/10 blur-3xl" />;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
  } else {
    t11 = $[14];
    t12 = $[15];
    t13 = $[16];
  }
  let t14;
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <img src={DEMO_COVER_PHOTO.url} alt="" className="absolute inset-0 h-full w-full object-cover" style={{
      objectPosition: DEMO_COVER_PHOTO.focus
    }} />;
    t15 = <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(30,23,16,0.18)_0%,rgba(30,23,16,0.42)_48%,rgba(20,16,12,0.84)_100%)]" />;
    $[17] = t14;
    $[18] = t15;
  } else {
    t14 = $[17];
    t15 = $[18];
  }
  let t16;
  if ($[19] !== eventName) {
    t16 = <div className="min-w-0 flex-1"><p className="text-3xl font-semibold tracking-[-0.03em] text-white">{eventName}</p></div>;
    $[19] = eventName;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const t17 = localeFlags[locale as keyof typeof localeFlags] ?? localeFlags.en;
  let t18;
  if ($[21] !== t17) {
    t18 = <span>{t17}</span>;
    $[21] = t17;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ChevronDown className="h-4 w-4" />;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] !== t18) {
    t20 = <span className="inline-flex h-12 shrink-0 items-center gap-1 rounded-full border border-white/20 px-3 text-sm font-medium text-white shadow-[0_16px_35px_-24px_rgba(0,0,0,0.45)] backdrop-blur-xl">{t18}{t19}</span>;
    $[24] = t18;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  let t21;
  if ($[26] !== t16 || $[27] !== t20) {
    t21 = <div className="flex items-start justify-between gap-2">{t16}{t20}</div>;
    $[26] = t16;
    $[27] = t20;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] !== locale || $[30] !== memoryCount) {
    t22 = new Intl.NumberFormat(locale).format(memoryCount);
    $[29] = locale;
    $[30] = memoryCount;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== t22) {
    t23 = <p className="text-2xl font-semibold tabular-nums tracking-[-0.03em] text-white">{t22}</p>;
    $[32] = t22;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] !== t) {
    t24 = t("Header.media.memories");
    $[34] = t;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] !== t24) {
    t25 = <p className="text-xs uppercase tracking-[0.18em] text-white/70">{t24}</p>;
    $[36] = t24;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] !== t23 || $[39] !== t25) {
    t26 = <div className="flex-1 text-left">{t23}{t25}</div>;
    $[38] = t23;
    $[39] = t25;
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  const t27 = stage === "gallery" && "scale-95";
  let t28;
  if ($[41] !== t27) {
    t28 = cn("relative isolate ml-auto inline-flex h-14 items-center justify-center overflow-hidden whitespace-nowrap rounded-full border border-transparent bg-transparent px-8 text-base font-medium text-white shadow-[0_16px_40px_-24px_rgba(0,0,0,0.8)] transition-transform duration-200 before:absolute before:inset-[-185%] before:-z-20 before:animate-[spin_4.8s_linear_infinite] before:bg-[conic-gradient(from_180deg,rgba(255,255,255,0.015)_0deg,rgba(255,255,255,0.2)_78deg,rgba(255,221,190,0.28)_112deg,rgba(255,255,255,0.03)_162deg,rgba(255,255,255,0.015)_360deg)] before:blur-[1.5px] before:content-[''] after:absolute after:inset-[2px] after:-z-10 after:rounded-full after:border after:border-white/40 after:backdrop-blur-xl after:content-[''] [&>*]:relative [&>*]:z-10", t27);
    $[41] = t27;
    $[42] = t28;
  } else {
    t28 = $[42];
  }
  let t29;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <CloudUpload className="mr-2 h-4 w-4" />;
    $[43] = t29;
  } else {
    t29 = $[43];
  }
  let t30;
  if ($[44] !== t) {
    t30 = t("UploadButton.upload");
    $[44] = t;
    $[45] = t30;
  } else {
    t30 = $[45];
  }
  let t31;
  if ($[46] !== t30) {
    t31 = <span>{t30}</span>;
    $[46] = t30;
    $[47] = t31;
  } else {
    t31 = $[47];
  }
  let t32;
  if ($[48] !== t28 || $[49] !== t31) {
    t32 = <span className={t28}>{t29}{t31}</span>;
    $[48] = t28;
    $[49] = t31;
    $[50] = t32;
  } else {
    t32 = $[50];
  }
  let t33;
  if ($[51] !== t26 || $[52] !== t32) {
    t33 = <div className="flex items-center gap-4">{t26}{t32}</div>;
    $[51] = t26;
    $[52] = t32;
    $[53] = t33;
  } else {
    t33 = $[53];
  }
  let t34;
  if ($[54] !== t21 || $[55] !== t33) {
    t34 = <div className="relative isolate overflow-hidden rounded-[30px] border border-white/35 bg-[#2b231d] shadow-[0_25px_80px_-45px_rgba(29,21,14,0.95)]">{t14}{t15}<div className="relative flex min-h-[22rem] flex-col justify-between gap-8 px-5 pb-4 pt-5">{t21}{t33}</div></div>;
    $[54] = t21;
    $[55] = t33;
    $[56] = t34;
  } else {
    t34 = $[56];
  }
  let t35;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Filter className="h-4 w-4 text-gray-500" />;
    $[57] = t35;
  } else {
    t35 = $[57];
  }
  let t36;
  if ($[58] !== t) {
    t36 = t("Sort.latest");
    $[58] = t;
    $[59] = t36;
  } else {
    t36 = $[59];
  }
  let t37;
  if ($[60] !== t36) {
    t37 = <span>{t36}</span>;
    $[60] = t36;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  let t38;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <ChevronDown className="h-4 w-4 opacity-50" />;
    $[62] = t38;
  } else {
    t38 = $[62];
  }
  let t39;
  if ($[63] !== t37) {
    t39 = <div className="pt-4"><div className="flex h-12 w-full items-center justify-between rounded-full border border-white/55 px-4 text-sm text-[#2f2922] shadow-[0_16px_32px_-26px_rgba(56,40,22,0.16)] backdrop-blur-xl">{t35}{t37}{t38}</div></div>;
    $[63] = t37;
    $[64] = t39;
  } else {
    t39 = $[64];
  }
  let t40;
  if ($[65] !== t34 || $[66] !== t39) {
    t40 = <div className="px-3 pt-3">{t34}{t39}</div>;
    $[65] = t34;
    $[66] = t39;
    $[67] = t40;
  } else {
    t40 = $[67];
  }
  let t41;
  if ($[68] !== hasNewTiles) {
    t41 = [...(hasNewTiles ? PICKED_PHOTOS : []), ...GALLERY_PHOTOS].slice(0, 12);
    $[68] = hasNewTiles;
    $[69] = t41;
  } else {
    t41 = $[69];
  }
  let t42;
  if ($[70] !== hasNewTiles || $[71] !== t || $[72] !== t41) {
    t42 = <div className="w-full px-3 pb-4 pt-4"><div className="grid grid-cols-3 gap-2">{t41.map((photo, index_0) => {
          const isOwn = hasNewTiles && index_0 < PICKED_PHOTOS.length;
          return <div key={`${photo.id}-${isOwn}`} className={cn("group relative aspect-square overflow-hidden rounded-sm bg-gray-100", isOwn && "motion-safe:animate-gallery-arrive")} style={isOwn ? {
            animationDelay: `${index_0 * 70}ms`
          } : undefined}><img src={photo.thumbnailUrl} alt="" className="h-full w-full object-cover" />{isOwn && <><span className="absolute right-0 top-0 z-10 rounded-bl-lg bg-gg-orange-500 px-2 py-1 text-[8px] text-white">{t("myUploadLabel")}</span><span className="pointer-events-none absolute inset-0 z-20 hidden rounded-sm ring-[3px] ring-inset ring-gg-orange-500 motion-safe:block motion-safe:animate-gallery-own-glow" /></>}</div>;
        })}</div></div>;
    $[70] = hasNewTiles;
    $[71] = t;
    $[72] = t41;
    $[73] = t42;
  } else {
    t42 = $[73];
  }
  let t43;
  if ($[74] !== t40 || $[75] !== t42) {
    t43 = <div className="relative flex h-full w-full flex-col overflow-hidden bg-[#f4efe8]" style={t11}>{t12}{t13}<div className="relative z-10">{t40}{t42}</div></div>;
    $[74] = t40;
    $[75] = t42;
    $[76] = t43;
  } else {
    t43 = $[76];
  }
  const t44 = isDialogShown ? "opacity-100" : "pointer-events-none opacity-0";
  let t45;
  if ($[77] !== t44) {
    t45 = cn("absolute inset-0 z-50 bg-black/80 transition-opacity duration-200", t44);
    $[77] = t44;
    $[78] = t45;
  } else {
    t45 = $[78];
  }
  const t46 = isDialogShown ? "scale-100" : "scale-95";
  let t47;
  if ($[79] !== t46) {
    t47 = cn("absolute left-1/2 top-1/2 grid w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg transition-transform duration-200", t46);
    $[79] = t46;
    $[80] = t47;
  } else {
    t47 = $[80];
  }
  let t48;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <X className="absolute right-4 top-4 h-4 w-4 opacity-70" />;
    $[81] = t48;
  } else {
    t48 = $[81];
  }
  let t49;
  if ($[82] !== isUploading || $[83] !== progress || $[84] !== savedCount || $[85] !== stage || $[86] !== t || $[87] !== tiles) {
    t49 = stage === "done" ? <div className="flex min-w-0 flex-col items-center justify-center p-2 text-center"><UploadSuccessFan previews={PICKED_PREVIEWS} /><p className="mb-2 text-xl font-semibold">{t("UploadButton.UploadModal.success.title", {
          count: PICKED_PHOTOS.length
        })}</p><p className="text-sm text-muted-foreground">{t("UploadButton.UploadModal.success.description")}</p><div className="mt-6 flex w-full flex-col"><span className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">{t("UploadButton.UploadModal.success.close")}</span></div></div> : <><div className="flex flex-col space-y-1.5 text-center"><p className="text-lg font-semibold leading-none tracking-tight">{t("UploadButton.UploadModal.title")}</p><p className="text-sm text-muted-foreground">{t("UploadButton.UploadModal.description")}</p></div><div className="max-h-[252px] w-full overflow-hidden rounded-2xl border border-[#e8dfd2] bg-[#f6f1ea] px-3 py-4"><div className="flex flex-wrap justify-center gap-x-3 gap-y-4">{PICKED_PHOTOS.map((photo_0, index_1) => <UploadPrintTile key={photo_0.id} fileId={`demo-${photo_0.id}`} name={photo_0.alt} isVideo={false} status={tiles[index_1].status} percentage={tiles[index_1].percentage} preview={{
            url: photo_0.thumbnailUrl
          }} landDelayMs={isUploading ? 80 + index_1 * 26 : undefined} />)}</div></div><UploadProgressMeter progress={progress} savedCount={savedCount} totalCount={PICKED_PHOTOS.length} isFinishing={progress >= 100 && savedCount < PICKED_PHOTOS.length} /></>;
    $[82] = isUploading;
    $[83] = progress;
    $[84] = savedCount;
    $[85] = stage;
    $[86] = t;
    $[87] = tiles;
    $[88] = t49;
  } else {
    t49 = $[88];
  }
  let t50;
  if ($[89] !== t47 || $[90] !== t49) {
    t50 = <div className={t47}>{t48}{t49}</div>;
    $[89] = t47;
    $[90] = t49;
    $[91] = t50;
  } else {
    t50 = $[91];
  }
  let t51;
  if ($[92] !== stage) {
    t51 = stage === "done" && <div className="pointer-events-none absolute left-1/2 top-[38%]">{CONFETTI.map(_temp2)}</div>;
    $[92] = stage;
    $[93] = t51;
  } else {
    t51 = $[93];
  }
  let t52;
  if ($[94] !== t45 || $[95] !== t50 || $[96] !== t51) {
    t52 = <div className={t45}>{t50}{t51}</div>;
    $[94] = t45;
    $[95] = t50;
    $[96] = t51;
    $[97] = t52;
  } else {
    t52 = $[97];
  }
  const t53 = stage === "picking" ? "translate-y-0" : "translate-y-[105%]";
  let t54;
  if ($[98] !== t53) {
    t54 = cn("absolute inset-x-0 bottom-0 z-40 h-[92%] rounded-t-[12px] bg-white shadow-[0_-12px_30px_-12px_rgba(0,0,0,0.35)] transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]", t53);
    $[98] = t53;
    $[99] = t54;
  } else {
    t54 = $[99];
  }
  let t55;
  if ($[100] !== pickerLabels.cancel) {
    t55 = <span className="text-[#007aff]">{pickerLabels.cancel}</span>;
    $[100] = pickerLabels.cancel;
    $[101] = t55;
  } else {
    t55 = $[101];
  }
  let t56;
  if ($[102] !== pickerLabels.title) {
    t56 = <span className="font-semibold text-black">{pickerLabels.title}</span>;
    $[102] = pickerLabels.title;
    $[103] = t56;
  } else {
    t56 = $[103];
  }
  let t57;
  if ($[104] !== pickerLabels.add) {
    t57 = <span className="font-semibold text-[#007aff]">{pickerLabels.add} ({PICKED_PHOTOS.length})</span>;
    $[104] = pickerLabels.add;
    $[105] = t57;
  } else {
    t57 = $[105];
  }
  let t58;
  if ($[106] !== t55 || $[107] !== t56 || $[108] !== t57) {
    t58 = <div className="flex items-center justify-between px-4 pb-3 pt-4 text-[17px]">{t55}{t56}{t57}</div>;
    $[106] = t55;
    $[107] = t56;
    $[108] = t57;
    $[109] = t58;
  } else {
    t58 = $[109];
  }
  let t59;
  if ($[110] !== stage) {
    t59 = PICKER_PHOTOS.map((photo_1, index_3) => {
      const pickIndex = PICKED_PHOTOS.indexOf(photo_1);
      return <div key={photo_1.id} className="relative aspect-square overflow-hidden"><img src={photo_1.thumbnailUrl} alt="" className="h-full w-full object-cover" />{pickIndex >= 0 && <span className={cn("absolute bottom-1.5 right-1.5 grid h-6 w-6 place-items-center rounded-full border-2 border-white bg-[#007aff] text-[13px] font-semibold text-white", stage === "picking" ? "motion-safe:animate-[phone-pick_300ms_cubic-bezier(0.34,1.56,0.64,1)_both]" : "opacity-0")} style={{
          animationDelay: `${450 + pickIndex * 420 + index_3 * 10}ms`
        }}>{pickIndex + 1}</span>}</div>;
    });
    $[110] = stage;
    $[111] = t59;
  } else {
    t59 = $[111];
  }
  let t60;
  if ($[112] !== t59) {
    t60 = <div className="grid grid-cols-3 gap-[2px]">{t59}</div>;
    $[112] = t59;
    $[113] = t60;
  } else {
    t60 = $[113];
  }
  let t61;
  if ($[114] !== t54 || $[115] !== t58 || $[116] !== t60) {
    t61 = <div className={t54}>{t58}{t60}</div>;
    $[114] = t54;
    $[115] = t58;
    $[116] = t60;
    $[117] = t61;
  } else {
    t61 = $[117];
  }
  const t62 = stage === "scan" ? "opacity-100" : "pointer-events-none opacity-0";
  let t63;
  if ($[118] !== t62) {
    t63 = cn("absolute inset-0 z-[60] bg-[#0d0b0a] transition-opacity duration-500 ease-out", t62);
    $[118] = t62;
    $[119] = t63;
  } else {
    t63 = $[119];
  }
  let t64;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <img src={DEMO_PHOTOS[18].url} alt="" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-45 blur-[6px]" />;
    $[120] = t64;
  } else {
    t64 = $[120];
  }
  let t65;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <div className="absolute left-1/2 top-[40%] -translate-x-1/2 -translate-y-1/2"><div className="relative rounded-2xl bg-black/50 p-5"><QRCode value="https://guest.gallery" qrStyle="dots" eyeRadius={4} fgColor="#FFFFFF" bgColor="transparent" size={150} quietZone={0} ecLevel="M" />{["left-0 top-0 border-l-4 border-t-4", "right-0 top-0 border-r-4 border-t-4", "bottom-0 left-0 border-b-4 border-l-4", "bottom-0 right-0 border-b-4 border-r-4"].map(_temp3)}</div></div>;
    $[121] = t65;
  } else {
    t65 = $[121];
  }
  const t66 = stage === "scan" && "motion-safe:animate-[phone-link-in_450ms_cubic-bezier(0.22,1,0.36,1)_1100ms_both]";
  let t67;
  if ($[122] !== t66) {
    t67 = cn("rounded-full bg-[#FFD60A] px-4 py-1.5 text-[15px] font-semibold text-black shadow-lg", t66);
    $[122] = t66;
    $[123] = t67;
  } else {
    t67 = $[123];
  }
  let t68;
  if ($[124] !== t67) {
    t68 = <div className="absolute inset-x-0 top-[62%] flex justify-center"><span className={t67}>guest.gallery</span></div>;
    $[124] = t67;
    $[125] = t68;
  } else {
    t68 = $[125];
  }
  let t69;
  if ($[126] !== t63 || $[127] !== t68) {
    t69 = <div className={t63}>{t64}{t65}{t68}</div>;
    $[126] = t63;
    $[127] = t68;
    $[128] = t69;
  } else {
    t69 = $[128];
  }
  const t70 = stage === "scan" || isDialogShown ? "text-white" : "text-black";
  let t71;
  if ($[129] !== t70) {
    t71 = cn("absolute inset-x-0 top-0 z-[70] flex items-center justify-between px-8 text-[16px] font-semibold", t70);
    $[129] = t70;
    $[130] = t71;
  } else {
    t71 = $[130];
  }
  let t72;
  let t73;
  if ($[131] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = {
      height: STATUS_BAR_HEIGHT
    };
    t73 = <span>21:14</span>;
    $[131] = t72;
    $[132] = t73;
  } else {
    t72 = $[131];
    t73 = $[132];
  }
  let t74;
  if ($[133] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <span className="flex items-end gap-[2px]">{[5, 7, 9, 11].map(_temp4)}</span>;
    $[133] = t74;
  } else {
    t74 = $[133];
  }
  let t75;
  if ($[134] === Symbol.for("react.memo_cache_sentinel")) {
    t75 = <span className="flex items-center gap-1.5">{t74}<span className="relative ml-1 h-[12px] w-[24px] rounded-[4px] border border-current p-[1.5px]"><span className="block h-full w-[75%] rounded-[2px] bg-current" /></span></span>;
    $[134] = t75;
  } else {
    t75 = $[134];
  }
  let t76;
  if ($[135] !== t71) {
    t76 = <div className={t71} style={t72}>{t73}{t75}</div>;
    $[135] = t71;
    $[136] = t76;
  } else {
    t76 = $[136];
  }
  let t77;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = <div className="absolute left-1/2 top-[11px] z-[80] h-[34px] w-[122px] -translate-x-1/2 rounded-full bg-black" />;
    $[137] = t77;
  } else {
    t77 = $[137];
  }
  let t78;
  if ($[138] !== t10 || $[139] !== t43 || $[140] !== t52 || $[141] !== t61 || $[142] !== t69 || $[143] !== t76) {
    t78 = <div ref={screenRef} className="relative w-full overflow-hidden rounded-[clamp(15px,2.6vw,31px)] bg-[#faf6f0]" style={t8}><div className="absolute left-0 top-0 origin-top-left overflow-hidden font-sans" style={t10}>{t43}{t52}{t61}{t69}{t76}{t77}</div></div>;
    $[138] = t10;
    $[139] = t43;
    $[140] = t52;
    $[141] = t61;
    $[142] = t69;
    $[143] = t76;
    $[144] = t78;
  } else {
    t78 = $[144];
  }
  let t79;
  if ($[145] === Symbol.for("react.memo_cache_sentinel")) {
    t79 = <style>{"\n        @keyframes phone-pick { from { opacity: 0; transform: scale(0.4); } }\n        @keyframes phone-link-in { from { opacity: 0; transform: translateY(16px) scale(0.96); } }\n        @keyframes phone-confetti {\n          0% { opacity: 0; transform: translate(0, 0) rotate(0deg); }\n          15% { opacity: 1; }\n          100% { opacity: 0; transform: translate(var(--confetti-x), 260px) rotate(var(--confetti-rotate)); }\n        }\n      "}</style>;
    $[145] = t79;
  } else {
    t79 = $[145];
  }
  let t80;
  if ($[146] !== t7 || $[147] !== t78) {
    t80 = <div aria-hidden="true" className={t7}>{t78}{t79}</div>;
    $[146] = t7;
    $[147] = t78;
    $[148] = t80;
  } else {
    t80 = $[148];
  }
  return t80;
}
function _temp4(height) {
  return <span key={height} className="w-[3px] rounded-[1px] bg-current" style={{
    height
  }} />;
}
function _temp3(corner) {
  return <span key={corner} className={cn("absolute h-8 w-8 rounded-[4px] border-[#FFD60A]", corner)} />;
}
function _temp2(piece, index_2) {
  return <span key={index_2} className="absolute h-2.5 w-1.5 rounded-[1px] motion-safe:animate-[phone-confetti_1200ms_cubic-bezier(0.2,0.7,0.3,1)_both] motion-reduce:hidden" style={{
    backgroundColor: piece.color,
    animationDelay: `${piece.delay}ms`,
    "--confetti-x": `${piece.x * 3.4}px`,
    "--confetti-rotate": `${piece.rotate}deg`
  } as React.CSSProperties} />;
}
function _temp(tile) {
  return tile.status === "success";
}